/**
 * The records a shareholder can also be, for the link picker.
 *
 * Two kinds, and they are the two the design draws the "Also in Ownership" tag
 * on: a lead contact and an ambassador. Subscribed on demand like the task
 * picker next door — a register of a dozen people should not read the lead book
 * to populate a dropdown nobody has opened.
 *
 * Leads are matched by company here rather than by contact, which is a
 * Decision 48 settles which are offered: a **company** links to the lead itself,
 * a **person** to a lead contact, an ambassador or a member of the team. The
 * picker asks the kind first, so this hook is only ever asked for one of them.
 */
import { useEffect, useState } from 'react'
import { subscribeToLeads } from '@/features/leads/repository/leadsRepository'
import { subscribeToEvents } from '@/features/events/repository/eventsRepository'
import { subscribeToCrmUsers } from '@/features/auth/repository/crmUserRepository'

/**
 * What a holder may also be, which follows what kind of holder it is.
 *
 * A **company** links to the lead itself — it cannot be a contact on itself. A
 * **person** links to a contact on a lead, to an ambassador, or to a member of
 * the team, because employees hold shares too. Decision 48.
 */
export type LinkKind = 'none' | 'lead' | 'contact' | 'ambassador' | 'team'

export interface LinkTarget {
  id: string
  label: string
  /** For a contact: which lead it belongs to, so the link keeps both. */
  leadId?: string
  leadName?: string
}

const byLabel = (a: LinkTarget, b: LinkTarget) => a.label.localeCompare(b.label)

export function useShareholderLinks(kind: LinkKind): {
  targets: LinkTarget[]
  loading: boolean
} {
  const [targets, setTargets] = useState<LinkTarget[]>([])
  const [loading, setLoading] = useState(kind !== 'none')

  useEffect(() => {
    if (kind === 'none') {
      setTargets([])
      setLoading(false)
      return
    }
    setLoading(true)
    const done = (rows: LinkTarget[]) => {
      setTargets(rows.sort(byLabel))
      setLoading(false)
    }
    const fail = () => setLoading(false)

    if (kind === 'lead') {
      return subscribeToLeads((leads) => done(leads.map((l) => ({ id: l.id, label: l.name }))), fail)
    }
    if (kind === 'contact') {
      /**
       * Every named contact across the book, labelled with its company so two
       * people called Ola are told apart.
       *
       * **Keyed on the email address**, because a contact has no id of its own —
       * it is an entry in an array on the lead. Decision 48 says links key on an
       * id rather than a name, and this is the closest thing a contact has: an
       * index would move the moment somebody reorders the list, and a name is
       * exactly what the rule forbids. A contact with no email cannot be linked
       * and is left out of the picker rather than offered and then lost.
       */
      return subscribeToLeads(
        (leads) =>
          done(
            leads.flatMap((l) =>
              (l.contacts ?? [])
                .filter((c) => c.email.trim())
                .map((c) => ({
                  id: c.email.trim().toLowerCase(),
                  label: `${c.name} — ${l.name}`,
                  leadId: l.id,
                  leadName: l.name,
                })),
            ),
          ),
        fail,
      )
    }
    if (kind === 'team') {
      return subscribeToCrmUsers(
        (users) => done(users.map((u) => ({ id: u.email, label: u.displayName || u.email }))),
        fail,
      )
    }
    return subscribeToEvents(
      (d) =>
        done(
          d.people
            .filter((p) => p.role === 'ambassador' || p.role === 'pilot')
            .map((p) => ({ id: p.id, label: p.name })),
        ),
      fail,
    )
  }, [kind])

  return { targets, loading }
}
